import React, { useState, useEffect } from 'react';
import { getAuth, onAuthStateChanged, signInWithEmailAndPassword } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { app } from '../firebase/authConfig';
import { REGION_FULL } from '../constants';
import { logger } from '../utils/logger';
import { secureLogAuthEvent } from '../utils/secureApiClient';

// ✅ Updated: Auth logging now uses secure token-based API calls
// Password is NEVER sent to the server for logging
// Only Firebase ID token (JWT access token) is sent

const P = {
  red:'#C62828', redD:'#B71C1C', redL:'#FFEBEE', redB:'#FFCDD2',
  cream:'#FFFAF5', beige:'#F1E6D8', beigeD:'#EDE4D8', beigeB:'#DDD0C0',
  white:'#FFFFFF', border:'#EAE3DA',
  text:'#1A0E06', sub:'#4A3728', muted:'#6B4F3A',
  font:"'DM Sans', 'Segoe UI', sans-serif",
  fontSerif:"'DM Serif Display', Georgia, serif",
};

export default function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPass, setShowPass] = useState(false);

  const navigate = useNavigate();
  const auth = getAuth(app);

  // ✅ Check auth state in background WITHOUT BLOCKING FORM RENDER
  // Show form immediately (within 500ms) even if Firebase is slow
  useEffect(() => {
    let authCheckTimeout;
    
    // Force ready flag after 500ms max, even if onAuthStateChanged hasn't fired
    authCheckTimeout = setTimeout(() => {
      // Form will show regardless
    }, 500);

    const unsub = onAuthStateChanged(auth, user => {
      clearTimeout(authCheckTimeout);
      if (user) {
        navigate('/official', { replace: true });
      }
    });

    return () => {
      clearTimeout(authCheckTimeout);
      unsub();
    };
  }, [auth, navigate]);

  async function handleLogin(e) {
    e.preventDefault();
    if (!username.trim() || !password.trim()) return;

    setLoading(true);
    setError('');

    const email = username.trim();

    // 🔐 SECURITY: We do NOT log login attempts because:
    // 1. Password would be visible in Network tab (security hint for attackers)
    // 2. Failed attempts with wrong passwords create an attack pattern
    // 3. Logging happens AFTER authentication is successful (with token only)
    logger.log('LoginPage', 'Attempting authentication...');

    try {
      // ✅ Step 1: Authenticate with email/password
      // Password is sent ONLY to Firebase (encrypted with HTTPS)
      // Firebase returns an ID token (JWT access token)
      // NOTE: This Firebase auth request is unavoidable - password must be verified
      const userCred = await signInWithEmailAndPassword(auth, email, password);

      // ✅ Step 2: Get the ID token (JWT access token)
      // This token is now used for all server requests, NOT the password
      const idToken = await userCred.user.getIdToken(true);
      logger.log('LoginPage', '✓ Access token obtained', idToken.substring(0, 30) + '...');

      // ✅ Step 3: Clear password from memory immediately
      // Password is no longer needed - token will be used instead
      setUsername('');
      setPassword('');

      // ✅ Step 4: Log successful login using secure token ONLY
      // ⚠️ IMPORTANT: Only log AFTER authentication succeeds
      // This way, failed attempts are NOT logged at all
      // Attackers can't see what passwords were tried
      secureLogAuthEvent('LOGIN_SUCCESS', userCred.user.email, 'SUCCESS').catch(err => {
        logger.warn('LoginPage', 'Failed to log success event', err?.message);
        // Continue even if logging fails - don't block user login
      });

      logger.log('LoginPage', `✓ User authenticated: ${userCred.user.email}`);
      logger.log('LoginPage', '✓ All future API requests will use access token, NOT password');

      navigate('/official', { replace: true });
    } catch (err) {
      logger.error('LoginPage', 'Authentication failed', err.code, err.message);

      // 🔐 SECURITY: We do NOT log failed login attempts
      // Reason: This prevents attackers from seeing:
      // - What email addresses exist
      // - What passwords were tried
      // - Attack patterns in failed attempts
      //
      // Server-side: Failed logins are still logged by Firebase
      // This is safer because password is not sent in the log

      // ✅ Clear password on error (never store it)
      setPassword('');

      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setError('Mali ang email o password. Subukang muli.');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Masyadong maraming pagtatangka. Subukan muli mamaya.');
      } else if (err.code === 'auth/network-request-failed') {
        setError('Hindi makakonekta. Suriin ang internet connection.');
      } else {
        setError('May error sa pag-login. Subukang muli.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      fontFamily: P.font,
      position: 'relative',
      overflow: 'hidden',
      background: P.beige,
    }}>

      {/* Background */}
      <div style={{
        position: 'fixed',
        inset: 0,
        zIndex: 0,
        backgroundImage: `linear-gradient(135deg, ${P.redD} 0%, #8B1A1A 40%, #3D0A0A 100%)`,
      }}>
        <svg style={{ position: 'absolute', bottom: 0, left: 0, right: 0, width: '100%', opacity: 0.12 }} viewBox="0 0 1440 320" preserveAspectRatio="none">
          <path fill="#fff" d="M0,160L48,144C96,128,192,96,288,106.7C384,117,480,171,576,181.3C672,192,768,160,864,138.7C960,117,1056,107,1152,117.3C1248,128,1344,160,1392,176L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"/>
        </svg>
        <svg style={{ position: 'absolute', bottom: 0, left: 0, right: 0, width: '100%', opacity: 0.08 }} viewBox="0 0 1440 320" preserveAspectRatio="none">
          <path fill="#fff" d="M0,256L60,240C120,224,240,192,360,186.7C480,181,600,203,720,213.3C840,224,960,224,1080,208C1200,192,1320,160,1380,144L1440,128L1440,320L1380,320C1320,320,1200,320,1080,320C960,320,840,320,720,320C600,320,480,320,360,320C240,320,120,320,60,320L0,320Z"/>
        </svg>
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 39px, rgba(255,255,255,0.04) 40px),
                            repeating-linear-gradient(90deg, transparent, transparent 39px, rgba(255,255,255,0.04) 40px)`,
        }}/>
      </div>

      {/* Content */}
      <div style={{
        position: 'relative', zIndex: 1,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 24px',
      }}>

        {/* Brand header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 38, fontWeight: 900, color: '#fff', letterSpacing: '0.1em', lineHeight: 1 }}>
            F.L.A.R.E.
          </div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', marginTop: 8, fontWeight: 500, letterSpacing: '0.04em' }}>
            Flood Level Alert and Risk Evaluation System
          </div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 4, fontWeight: 400 }}>
            {REGION_FULL}
          </div>
        </div>

        {/* Login card */}
        <div style={{
          width: '100%',
          maxWidth: 420,
          background: P.white,
          boxShadow: '0 32px 80px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.1)',
          overflow: 'hidden',
          animation: 'slideUp 0.4s cubic-bezier(0.34,1.1,0.64,1)',
        }}>

          {/* Card header */}
          <div style={{
            background: P.red,
            padding: '18px 28px',
            borderBottom: `3px solid ${P.redD}`,
          }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#fff', lineHeight: 1 }}>Login para sa mga Opisyal</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.72)', marginTop: 3, fontWeight: 500 }}>
              Para sa mga opisyal ng barangay lamang
            </div>
          </div>

          {/* Card body */}
          <div style={{ padding: '32px 28px 28px' }}>

            {/* Badge */}
            <div style={{
              background: P.redL,
              border: `1px solid ${P.redB}`,
              padding: '10px 14px',
              marginBottom: 24,
            }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: P.red, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                PARA SA MGA ADMIN LAMANG
              </div>
              <div style={{ fontSize: 10, color: P.sub, marginTop: 2, fontWeight: 500 }}>
                Ang page na ito ay para lamang sa mga awtorisadong opisyal
              </div>
            </div>

            <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{
                  fontSize: 10, fontWeight: 800, color: P.sub,
                  textTransform: 'uppercase', letterSpacing: '0.08em',
                  display: 'block', marginBottom: 7,
                }}>EMAIL</label>
                <input
                  type="email"
                  value={username}
                  onChange={e => { setUsername(e.target.value); setError(''); }}
                  placeholder="opisyal@barangay.gov.ph"
                  autoComplete="email"
                  required
                  style={{
                    width: '100%', padding: '12px 14px',
                    border: `1.5px solid ${P.beigeB}`,
                    fontSize: 13, color: P.text, outline: 'none',
                    background: P.beige, boxSizing: 'border-box',
                    transition: 'border-color 0.15s, box-shadow 0.15s',
                    fontFamily: P.font,
                  }}
                  onFocus={e => { e.target.style.borderColor = P.red; e.target.style.boxShadow = `0 0 0 3px ${P.redL}`; }}
                  onBlur={e => { e.target.style.borderColor = P.beigeB; e.target.style.boxShadow = 'none'; }}
                />
              </div>

              <div>
                <label style={{
                  fontSize: 10, fontWeight: 800, color: P.sub,
                  textTransform: 'uppercase', letterSpacing: '0.08em',
                  display: 'block', marginBottom: 7,
                }}>Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPass ? 'text' : 'password'}
                    value={password}
                    onChange={e => { setPassword(e.target.value); setError(''); }}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    required
                    style={{
                      width: '100%', padding: '12px 44px 12px 14px',
                      border: `1.5px solid ${P.beigeB}`,
                      fontSize: 13, color: P.text, outline: 'none',
                      background: P.beige, boxSizing: 'border-box',
                      transition: 'border-color 0.15s, box-shadow 0.15s',
                      fontFamily: P.font,
                    }}
                    onFocus={e => { e.target.style.borderColor = P.red; e.target.style.boxShadow = `0 0 0 3px ${P.redL}`; }}
                    onBlur={e => { e.target.style.borderColor = P.beigeB; e.target.style.boxShadow = 'none'; }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(s => !s)}
                    style={{
                      position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                      background: 'none', border: 'none', cursor: 'pointer',
                      fontSize: 11, color: P.muted, padding: '4px', fontFamily: P.font, fontWeight: 600,
                    }}
                  >{showPass ? 'ITAGO' : 'IPAKITA'}</button>
                </div>
              </div>

              {error && (
                <div style={{
                  background: P.redL, border: `1px solid ${P.redB}`,
                  padding: '11px 14px', fontSize: 12,
                  color: P.redD, fontWeight: 600,
                }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                style={{
                  background: loading ? P.muted : P.red,
                  color: '#fff', border: 'none',
                  padding: '13px 18px', fontSize: 13,
                  fontWeight: 800, cursor: loading ? 'not-allowed' : 'pointer',
                  marginTop: 4, transition: 'background 0.15s',
                  letterSpacing: '0.04em', fontFamily: P.font,
                }}
                onMouseEnter={e => { if (!loading) e.currentTarget.style.background = P.redD; }}
                onMouseLeave={e => { if (!loading) e.currentTarget.style.background = loading ? P.muted : P.red; }}
              >
                {loading ? 'Naglo-login...' : 'Mag-login sa Admin Portal'}
              </button>

              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                style={{
                  background: 'none', border: 'none',
                  color: P.muted, fontSize: 12,
                  cursor: 'pointer', padding: '4px',
                  fontFamily: P.font, fontWeight: 600,
                  textAlign: 'center',
                  textDecoration: 'underline',
                  textDecorationColor: 'transparent',
                  transition: 'color 0.15s, text-decoration-color 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.color = P.red; e.currentTarget.style.textDecorationColor = P.red; }}
                onMouseLeave={e => { e.currentTarget.style.color = P.muted; e.currentTarget.style.textDecorationColor = 'transparent'; }}
              >
                Nakalimutan ang password?
              </button>
            </form>

            <div style={{
              marginTop: 20, paddingTop: 16,
              borderTop: `1px solid ${P.border}`,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
            }}>
              <div style={{ fontSize: 10, color: P.muted, lineHeight: 1.6 }}>
                Para sa awtorisadong opisyal lamang<br/>
                Bawal ang pag-access nang walang<br/>
                pahintulot
              </div>
              <button
                onClick={() => navigate('/')}
                style={{
                  background: 'none', border: `1px solid ${P.beigeB}`,
                  color: P.sub, fontSize: 11, fontWeight: 600,
                  padding: '7px 14px', cursor: 'pointer',
                  transition: 'all 0.15s', fontFamily: P.font,
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = P.red; e.currentTarget.style.color = P.red; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = P.beigeB; e.currentTarget.style.color = P.sub; }}
              >
                Bumalik sa Dashboard
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ marginTop: 28, textAlign: 'center' }}>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', lineHeight: 1.8 }}>
            F.L.A.R.E. · Flood Level Alert and Risk Evaluation System<br />
            {REGION_FULL} · {new Date().getFullYear()}
          </div>
        </div>

      </div>

      <style>{`
        * { box-sizing: border-box; }
        body { font-family: 'DM Sans', 'Segoe UI', sans-serif; -webkit-font-smoothing: antialiased; margin: 0; }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(28px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        input, button, textarea, select { font-family: 'DM Sans', 'Segoe UI', sans-serif; }
        ::-webkit-scrollbar { width: 7px; }
        ::-webkit-scrollbar-track { background: ${P.beige}; }
        ::-webkit-scrollbar-thumb { background: ${P.redB}; border-radius: 99px; }
        ::-webkit-scrollbar-thumb:hover { background: ${P.red}; }
      `}</style>
    </div>
  );
}